import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Target, Plus, Edit2, Trash2, Calendar, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = [
  { value: 'not_started', label: 'Not Started', color: 'bg-slate-100 text-slate-700 border-slate-300' },
  { value: 'in_progress', label: 'In Progress', color: 'bg-[#FEF3C7] text-[#F59E0B] border-[#F59E0B]' },
  { value: 'completed', label: 'Completed', color: 'bg-[#D1FAE5] text-[#10B981] border-[#10B981]' },
  { value: 'blocked', label: 'Blocked', color: 'bg-[#FEE2E2] text-[#EF4444] border-[#EF4444]' },
];

const formatDate = (d) => (d ? new Date(d).toLocaleString() : '—');

const emptyForm = {
  title: '',
  description: '',
  target_date: '',
  status: 'not_started',
  progress: 0,
  status_note: '',
};

const GoalsSection = ({ isAdmin = false }) => {
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    fetchGoals();
  }, []);

  const fetchGoals = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/goals`, { withCredentials: true });
      setGoals(data);
    } catch (error) {
      toast.error('Failed to load goals');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setEditingGoal(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const handleOpenEdit = (goal) => {
    setEditingGoal(goal);
    setForm({
      title: goal.title || '',
      description: goal.description || '',
      target_date: goal.target_date || '',
      status: goal.status || 'not_started',
      progress: goal.progress ?? 0,
      status_note: goal.status_note || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, progress: parseInt(form.progress) || 0 };
      if (editingGoal) {
        await axios.put(`${API}/api/goals/${editingGoal.id}`, payload, { withCredentials: true });
        toast.success('Goal updated successfully');
      } else {
        await axios.post(`${API}/api/goals`, payload, { withCredentials: true });
        toast.success('Goal created successfully');
      }
      setDialogOpen(false);
      fetchGoals();
    } catch (error) {
      toast.error('Failed to save goal');
    }
  };

  const handleDelete = async (goalId) => {
    if (!window.confirm('Delete this goal?')) return;
    try {
      await axios.delete(`${API}/api/goals/${goalId}`, { withCredentials: true });
      toast.success('Goal deleted');
      fetchGoals();
    } catch (error) {
      toast.error('Failed to delete goal');
    }
  };

  const getStatusBadge = (status) => {
    const opt = STATUS_OPTIONS.find((s) => s.value === status) || STATUS_OPTIONS[0];
    return <Badge className={`${opt.color} border`} data-testid={`goal-status-${status}`}>{opt.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <Card className="bg-white border-slate-200" data-testid="goals-section">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <Target className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <CardTitle className="text-xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {isAdmin ? 'All Teacher Goals' : 'My Goals'}
              </CardTitle>
              <CardDescription>
                {isAdmin ? 'View and modify goals submitted by teachers' : 'Set goals, track progress, and update status'}
              </CardDescription>
            </div>
          </div>
          {!isAdmin && (
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleOpenCreate} data-testid="add-goal-button">
              <Plus className="w-4 h-4 mr-2" />
              Add Goal
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {goals.length === 0 ? (
          <div className="text-center py-12 text-slate-500" data-testid="no-goals-message">
            <Target className="w-12 h-12 mx-auto mb-3 text-slate-400" />
            <p>No goals {isAdmin ? 'submitted by teachers' : 'yet — click "Add Goal" to create one'}</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="goals-list">
            {goals.map((goal, idx) => (
              <div
                key={goal.id}
                className="p-5 border border-slate-200 rounded-lg hover:shadow-sm transition-shadow"
                data-testid={`goal-item-${idx}`}
              >
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold text-slate-900 text-lg" data-testid={`goal-title-${idx}`}>
                        {goal.title}
                      </h4>
                      {getStatusBadge(goal.status)}
                    </div>
                    {isAdmin && (
                      <p className="text-xs text-blue-600 font-medium mb-2">By: {goal.user_name}</p>
                    )}
                    {goal.description && (
                      <p className="text-sm text-slate-600 mb-2">{goal.description}</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleOpenEdit(goal)}
                      data-testid={`edit-goal-${idx}`}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    {(!isAdmin || goal.user_id) && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(goal.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-goal-${idx}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>

                {/* Progress bar */}
                <div className="mb-3">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Progress</span>
                    <span className="text-sm font-medium text-slate-700" data-testid={`goal-progress-${idx}`}>{goal.progress}%</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${goal.progress}%` }}
                    ></div>
                  </div>
                </div>

                {goal.status_note && (
                  <div className="mb-3 p-3 bg-slate-50 border border-slate-200 rounded-md">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-1">Latest Update</p>
                    <p className="text-sm text-slate-700">{goal.status_note}</p>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-slate-500 pt-3 border-t border-slate-100">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    <span>Target: {goal.target_date}</span>
                  </div>
                  <div>
                    <span className="font-semibold">Created:</span> {formatDate(goal.created_at)}
                  </div>
                  <div data-testid={`goal-updated-${idx}`}>
                    <span className="font-semibold">Last Updated:</span> {formatDate(goal.updated_at)}
                    {goal.last_modified_by && <span className="ml-1">by {goal.last_modified_by}</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Add/Edit Goal Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="goal-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
              {editingGoal ? 'Update Goal' : 'Add New Goal'}
            </DialogTitle>
            <DialogDescription>
              {editingGoal ? 'Update goal details and progress' : 'Set a new goal with target date and status'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4" data-testid="goal-form">
            <div>
              <Label htmlFor="title">Goal Title</Label>
              <Input
                id="title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                required
                placeholder="e.g., Complete advanced pedagogy course"
                data-testid="goal-title-input"
              />
            </div>
            <div>
              <Label htmlFor="description">Description (optional)</Label>
              <Textarea
                id="description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Additional details about the goal..."
                rows={2}
                data-testid="goal-description-input"
              />
            </div>
            <div>
              <Label htmlFor="target_date">Target Date</Label>
              <Input
                id="target_date"
                type="date"
                value={form.target_date}
                onChange={(e) => setForm({ ...form, target_date: e.target.value })}
                required
                data-testid="goal-target-date-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="status">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="goal-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="progress">Progress (%)</Label>
                <Input
                  id="progress"
                  type="number"
                  min="0"
                  max="100"
                  value={form.progress}
                  onChange={(e) => setForm({ ...form, progress: e.target.value })}
                  data-testid="goal-progress-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="status_note">Progress Update / Notes</Label>
              <Textarea
                id="status_note"
                value={form.status_note}
                onChange={(e) => setForm({ ...form, status_note: e.target.value })}
                placeholder="Describe latest progress or blockers..."
                rows={3}
                data-testid="goal-status-note-input"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} data-testid="cancel-goal-button">
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="save-goal-button">
                <CheckCircle className="w-4 h-4 mr-2" />
                {editingGoal ? 'Update Goal' : 'Create Goal'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default GoalsSection;
